import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import CallCreditGateModal from "../components/calls/CallCreditGateModal";
import { parseQuotaCredits } from "../utils/callCreditRules";

/**
 * Shows confirm / exhausted modals before marketplace or intro calls.
 * Returns true from runGatedCall when the wrapped action should proceed.
 */
export function useCallCreditGate({ refreshFreemium } = {}) {
  const navigate = useNavigate();
  const [gate, setGate] = useState(null);
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef(null);

  const closeGate = useCallback(() => {
    if (pendingRef.current?.resolve) {
      pendingRef.current.resolve(false);
    }
    pendingRef.current = null;
    setGate(null);
  }, []);

  const fetchQuota = useCallback(async () => {
    const res = await api.getIntroCallQuota();
    return res?.quota || res?.data?.quota || res;
  }, []);

  const openExhausted = useCallback(
    ({ quota, peerName, onRetry } = {}) => {
      setGate({
        mode: "exhausted",
        quota: quota || null,
        peerName: peerName || "",
        onRetry,
      });
    },
    []
  );

  const runGatedCall = useCallback(
    async ({ peerName, action }) => {
      if (typeof action !== "function") return false;
      let quota = null;
      try {
        quota = await fetchQuota();
      } catch {
        quota = null;
      }
      const { remaining } = parseQuotaCredits(quota);
      if (remaining < 1) {
        return new Promise((resolve) => {
          pendingRef.current = { resolve };
          setGate({
            mode: "exhausted",
            quota,
            peerName: peerName || "caregiver",
            onRetry: async () => {
              try {
                const next = await fetchQuota();
                const { remaining: left } = parseQuotaCredits(next);
                if (left >= 1) {
                  closeGate();
                  resolve(await action());
                  return true;
                }
                setGate((g) => (g ? { ...g, quota: next } : g));
                return false;
              } catch {
                return false;
              }
            },
          });
        });
      }

      return new Promise((resolve) => {
        pendingRef.current = { resolve };
        setGate({
          mode: "confirm",
          quota,
          peerName: peerName || "caregiver",
          action,
        });
      });
    },
    [closeGate, fetchQuota]
  );

  const confirmStart = useCallback(async () => {
    if (!gate?.action || busy) return;
    setBusy(true);
    try {
      const ok = await gate.action();
      pendingRef.current?.resolve?.(ok !== false);
      pendingRef.current = null;
      setGate(null);
      refreshFreemium?.();
    } finally {
      setBusy(false);
    }
  }, [gate, busy, refreshFreemium]);

  const handleTopUpSuccess = useCallback(
    async (nextQuota) => {
      let quota = nextQuota;
      if (!quota) {
        try {
          quota = await fetchQuota();
        } catch {
          quota = null;
        }
      }
      refreshFreemium?.();
      const { remaining } = parseQuotaCredits(quota);
      if (remaining >= 1 && gate?.onRetry) {
        await gate.onRetry();
        return;
      }
      if (remaining >= 1 && gate?.action) {
        setGate((g) => (g ? { ...g, mode: "confirm", quota } : g));
        return;
      }
      setGate((g) => (g ? { ...g, mode: "exhausted", quota } : g));
    },
    [fetchQuota, gate, refreshFreemium]
  );

  const gateModal = gate ? (
    <CallCreditGateModal
      mode={gate.mode}
      quota={gate.quota}
      peerName={gate.peerName}
      busy={busy}
      onBusyChange={setBusy}
      onConfirm={confirmStart}
      onCancel={closeGate}
      onTopUpSuccess={handleTopUpSuccess}
      onNavigateManage={() => {
        closeGate();
        navigate("/call-history");
      }}
    />
  ) : null;

  return {
    runGatedCall,
    openExhausted,
    closeGate,
    gateModal,
    fetchQuota,
  };
}
